import { useCallback, useEffect, useRef, useState } from 'react'
import catRunningBgm from './game/bgm'

export default function useCatRunningBgm({ shouldPlay, worldRef }) {
  const [muted, setMuted] = useState(() => catRunningBgm.loadMutedPreference())
  const lastPlayingStageKeyRef = useRef(null)
  const shouldPlayRef = useRef(shouldPlay)
  const mutedRef = useRef(muted)

  useEffect(() => {
    shouldPlayRef.current = shouldPlay
  }, [shouldPlay])

  useEffect(() => {
    mutedRef.current = muted
  }, [muted])

  useEffect(() => {
    catRunningBgm.preload()
    return () => {
      catRunningBgm.stop()
    }
  }, [])

  useEffect(() => {
    if (!shouldPlay || !worldRef) {
      catRunningBgm.stop()
      lastPlayingStageKeyRef.current = null
      return undefined
    }

    let frameId = 0
    let isActive = true

    const syncStageBgm = () => {
      if (!isActive) return

      const stageKey = worldRef.current?.stageKey
      if (!stageKey) {
        frameId = window.requestAnimationFrame(syncStageBgm)
        return
      }

      const isPlaying = catRunningBgm.isPlaying()
      const activeKey = catRunningBgm.getActiveStageKey()

      if (isPlaying && activeKey === stageKey) {
        lastPlayingStageKeyRef.current = stageKey
        frameId = window.requestAnimationFrame(syncStageBgm)
        return
      }

      // 시작 버튼에서 켠 트랙 → 실제 스테이지로만 교체. 끄고 다시 켜지 않는다.
      const shouldSwitchStage =
        isPlaying && activeKey && activeKey !== stageKey && catRunningBgm.isUnlocked()

      const shouldResume =
        !isPlaying && catRunningBgm.isUnlocked() && !mutedRef.current

      if (shouldSwitchStage || shouldResume) {
        catRunningBgm.playStage(stageKey).then((didPlay) => {
          if (!isActive) return
          if (didPlay) {
            lastPlayingStageKeyRef.current = stageKey
          }
        })
      }

      frameId = window.requestAnimationFrame(syncStageBgm)
    }

    syncStageBgm()

    return () => {
      isActive = false
      window.cancelAnimationFrame(frameId)
    }
  }, [shouldPlay, worldRef])

  // 만보기와 동일: 게임 중 화면 어디든 탭하면 재생 재시도
  useEffect(() => {
    if (!shouldPlay || muted) return undefined

    const unlock = () => {
      if (!shouldPlayRef.current || mutedRef.current) return
      const stageKey = worldRef.current?.stageKey
      catRunningBgm.unlockFromUserGesture(stageKey).then((didPlay) => {
        if (didPlay) {
          lastPlayingStageKeyRef.current = stageKey ?? catRunningBgm.getActiveStageKey()
        }
      })
    }

    document.addEventListener('pointerdown', unlock, { capture: true })
    document.addEventListener('touchstart', unlock, { capture: true })

    return () => {
      document.removeEventListener('pointerdown', unlock, { capture: true })
      document.removeEventListener('touchstart', unlock, { capture: true })
    }
  }, [muted, shouldPlay, worldRef])

  const unlockFromUserGesture = useCallback(() => {
    if (muted) return

    const stageKey = worldRef.current?.stageKey

    if (catRunningBgm.isPlaying() && (!stageKey || catRunningBgm.getActiveStageKey() === stageKey)) {
      catRunningBgm.ensureAudible()
      lastPlayingStageKeyRef.current = stageKey ?? catRunningBgm.getActiveStageKey()
      return
    }

    lastPlayingStageKeyRef.current = stageKey ?? lastPlayingStageKeyRef.current
    catRunningBgm.unlockFromUserGesture(stageKey).then((didPlay) => {
      if (didPlay && shouldPlayRef.current) {
        lastPlayingStageKeyRef.current = stageKey ?? catRunningBgm.getActiveStageKey()
      } else if (!didPlay) {
        lastPlayingStageKeyRef.current = null
      }
    })
  }, [muted, worldRef])

  // 스피커 버튼: 1회 음소거 / 2회 재생
  const toggleMuted = useCallback(() => {
    setMuted((current) => {
      const next = !current
      catRunningBgm.setMuted(next).then((didPlay) => {
        if (!next && didPlay) {
          lastPlayingStageKeyRef.current = worldRef.current?.stageKey ?? catRunningBgm.getActiveStageKey()
        }
      })
      return next
    })
  }, [worldRef])

  return {
    muted,
    toggleMuted,
    unlockFromUserGesture,
  }
}
